<template>
  <div class="c-found-your-team">
    <h1>We found your team!</h1>
    <p class="overview">
      Based on your email address, it looks like your company is already on
      Crossbeam. Request access to be approved by one of your team’s admins.
    </p>
    <BittsCallout
      v-if="rejectedRequest"
      data-testid="rejected-request-callout"
      type="danger"
      :title="`Your last request was rejected ${rejectedRequestDate}`"
      subtitle="Request access again to join your team on Crossbeam"
      class="mb-16"
    />
    <BittsCard class="p-24" :no-padding="false">
      <div class="flex justify-between">
        <div class="flex gap-12">
          <img
            v-if="logo"
            :src="logo"
            :alt="companyName"
            class="c-company-logo__img"
          />
          <div>
            <div class="company-name">
              {{ companyName }}
            </div>
            <div class="company-domain">
              {{ clearbitDomain }}
            </div>
          </div>
        </div>
        <BittsTag color="info"> Recommended </BittsTag>
      </div>
      <BittsTextArea
        v-model="message"
        data-testid="seat-request-message-input"
        :form-label
        class="my-24"
        :status="messageTooLong ? 'danger' : 'default'"
        danger-text="Your message may not exceed 200 characters"
        placeholder="Add a message to your request (you'll be more likely to be added)"
      />
      <BittsButton
        data-testid="request-to-join-button"
        class="w-full"
        :loading="loading || sendingRequest"
        :disabled="messageTooLong || loading || sendingRequest"
        text="Request to join"
        @click="handleSendSeatRequest"
      />
    </BittsCard>
    <p class="not-your-team">
      Not your team?
      <button
        class="action-button"
        @click="emit('reject-suggestion')"
        type="button"
      >
        Register a new company
      </button>
      or
      <button class="action-button" @click="contactSupport" type="button">
        contact our support team
      </button>
    </p>
    <BittsButton
      class="flex sm:hidden w-full"
      variant="outline"
      type="neutral"
      text="Log out"
      :right-icon="['fak', 'logout']"
      @click="handleLogout"
    />
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsCard,
  BittsTag,
  BittsTextArea,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import axios from 'axios';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, ref, toRefs } from 'vue';
import { useRouter } from 'vue-router';

import useIteratively from '@/composables/useIteratively';
import { CORE } from '@/constants/team';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import urls from '@/urls';
import { intercomOrEmailMessage } from '@/utils';

const props = withDefaults(
  defineProps<{
    companyName: string;
    logo: string | null;
    clearbitDomain: string;
    loading?: boolean;
  }>(),
  { loading: false },
);

const emit = defineEmits(['reject-suggestion']);

const { companyName, logo, clearbitDomain, loading } = toRefs(props);

const router = useRouter();
const flashesStore = useFlashesStore();
const rootStore = useRootStore();

const { seatRequests } = storeToRefs(rootStore);
const { iteratively } = useIteratively();

const message = ref('');
const formLabel = computed(() => ({
  title: 'Message to Admins',
  secondaryText: `${message.value.length}/200 characters`,
}));
const messageTooLong = computed(() => message.value.length > 200);

function contactSupport() {
  intercomOrEmailMessage(
    'support@crossbeam.com',
    'Registration Support',
    `Hello, I've been recommended to join ${companyName.value}, but this is not my company`,
  );
}

const rejectedRequest = computed(() =>
  seatRequests.value.find((r) => r.status === 'rejected'),
);
const rejectedRequestDate = computed(() =>
  rejectedRequest.value?.created_at
    ? DateTime.fromISO(rejectedRequest.value?.created_at).toFormat('DD')
    : '',
);

const sendingRequest = ref(false);
async function handleSendSeatRequest() {
  try {
    sendingRequest.value = true;
    const { data } = await axios.post(urls.seatRequests.create, {
      type: CORE,
      message: message.value,
    });
    trackSeatRequest(data.organization_id.toString());
    await axios.post(urls.inviteRequests.notify(data.id)); // Notify admins of the access request
    // @ts-expect-error - router.go() seems valid and works, but TS complains about it
    router.go(); // After the request is created, router guards will send us to 'invite-requests' with the UUID
  } catch (err) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong creating your seat request',
    });
    captureException(err);
  } finally {
    sendingRequest.value = false;
  }
}

async function handleLogout() {
  await router.push({ name: 'logout' });
}

function trackSeatRequest(requestedOrganizationId: string) {
  iteratively.userRequestedSeat({
    event_site: EVENT_SITES.FOUND_YOUR_TEAM,
    source: 'General Request',
    metadata: {
      message: message.value,
      company_name: companyName.value,
      clearbit_domain: clearbitDomain.value,
      requested_organization_id: requestedOrganizationId,
    },
  });
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-found-your-team {
  @apply max-w-[600px] mx-16;
  h1 {
    @apply text-neutral-text-strong text-xl text-center mb-16 font-bold;
  }

  p.overview {
    @apply text-neutral-text-weak text-m text-center mb-40;
  }

  .c-company-logo__img {
    @apply mb-16 h-40 w-40 rounded-8 border-neutral-border border;
  }

  .company-name {
    @apply text-neutral-text-strong text-m font-bold;
  }

  .company-domain {
    @apply text-neutral-text text-base;
  }

  .not-your-team {
    @apply mt-24 mb-40 sm:mb-0 sm:mt-40 text-neutral-text-weak text-center;
    .action-button {
      @apply text-secondary-text;
      &:hover {
        @apply opacity-80;
      }
    }
  }
}
</style>
